// ─────────────────────────────────────────────────────────────────────────────
// alertPageTourBeats — the script the buddy runs on /alerts/[id] right after
// the viewer arrives from the replay (via `?onboarding=1`).
//
// Each entry is a BuddyBeat matching the same contract BuddyOverlay renders
// for the replay tour. The runner (`useClientBuddyScript`) walks through these
// top-to-bottom; the viewer advances with Enter / click-outside / the bubble's
// "Next" button, or exits with Esc at any time.
//
// Narrative arc (post-2026-05 redesign, 17 beats):
//   1   Welcome — bottom-right bubble, no spotlight at all. Sets the scene.
//   2-4 Dashboard — click the final cell, scroll through it, close it.
//   5-7 History — click open, browse the run sidebar, close it.
//   8   Email chip — quick pointer at where notifications land.
//   9-12 Schedule — click chip, see presets, see plain-English input, close it.
//   13-16 Action cluster — Run Now, Pause, three-dot trigger, three-dot menu.
//   17  Chat input — final beat, "ask the alert anything from here".
//
// Visual conventions:
//   • Every beat after #1 uses `highlightOnly: true`. BuddyOverlay renders a
//     1.5px sky-blue ring on the anchor and a soft 42% black dim everywhere
//     else. No 78% blackout (the previous version dimmed too aggressively;
//     the user wants the rest of the page readable for context).
//   • Beats that need the user to click something pair `highlightOnly: true`
//     with `action: { kind: "click_target" }`. In that combo BuddyOverlay
//     suppresses the white ring and renders ONLY the emerald pulsing ring +
//     "Click here" pill, so we don't double up rings on the same target.
//   • Beat #1 (welcome) uses `noAnchor: true` + `bubblePlacement: "bottomRight"`
//     so the first impression is the live alert page, not a centered modal.
//   • All copy uses "**bold**" + "\n\n" paragraph breaks (BuddyOverlay
//     supports this lightweight markdown). Zero em-dashes. Every CTA is
//     "Next" or implicit (click_target). Single decision rather than a
//     zoo of "Got it" / "Show me inside" / "I've seen enough".
//
// Extending the tour:
//   • Add a new beat object to the array below.
//   • If your anchor needs a new `data-tour` / `data-testid` attribute,
//     add it to the target component FIRST (e.g. AlertTopBar,
//     AlertLogsModal, EditScheduleDialog) so the selector resolves on
//     first paint. Do NOT hard-code class-name selectors that only the
//     design system uses — they break silently the next time Tailwind
//     purges.
//   • `click_target` beats require the underlying element to be a button
//     or link whose native click does the work. The overlay's document-
//     level click listener releases the beat on the same event — no onClick
//     handler changes needed on the target.
//
// IMPORTANT: keep the `beatId` strings STABLE once shipped. They're written
// into telemetry + (eventually) the "seen beats" local-storage ledger for
// skip-on-return behaviour. Renaming would orphan those records.
//
// All beat IDs start with `alert_` so BuddyOverlay's `isAlertPhase` check
// (`beat.beatId.startsWith("alert_")`) auto-applies the icy/sky theme to
// the bubble — same visual language the replay's alert-mode beats use.
// ─────────────────────────────────────────────────────────────────────────────

import type { BuddyBeat } from "./BuddyOverlay";

/**
 * Build the beat sequence. A function (not a const) so callers can
 * personalize copy per-alert (e.g. interpolate the alert name into
 * the welcome beat) without mutating a shared array.
 *
 * @param alertName - Display name shown in the welcome beat. Falls
 *                    back to a generic phrasing when empty.
 * @param finalNodeIndex - 1-based displayIndex of the FINAL dashboard
 *                    node. Defaults to 5 to match the canonical
 *                    onboarding alert (`alert-437b57f91e11`, "Daily
 *                    S&P 500 Edge Briefing") AFTER the cleanup pass
 *                    trimmed it from 9 visible cells to 5. Pass a
 *                    different number when the alert has a different
 *                    shape. The runner doesn't validate this — if the
 *                    selector misses, the beat falls back to the
 *                    centered placement, the tour never soft-locks.
 */
export function buildAlertPageTourBeats(
  // alertName is intentionally unused by the welcome beat now (the
  // user explicitly killed the "<formal alert name> is live" framing
  // because it read as corporate / neutered research-speak). Kept in
  // the signature for forward-compat in case a future beat wants to
  // reference the configured name (e.g. a settings link). The
  // `_` prefix marks it as intentionally unused without depending
  // on a specific lint rule being enabled.
  _alertName?: string | null,
  finalNodeIndex: number = 5,
  options?: { includeSidebarOpener?: boolean },
): BuddyBeat[] {
  const sidebarOpenerBeats: BuddyBeat[] = options?.includeSidebarOpener
    ? [
        {
          beatId: "alert_sidebar_open",
          trigger: { type: "client" },
          copy: {
            title: "Open the sidebar",
            body:
              "This is your way back into the rest of Xynth. Open it whenever you want to jump between chats, alerts, and your account.",
          },
          anchor: { kind: "css", value: "[data-tour='sidebar-opener']" },
          spotlight: { kind: "css", value: "[data-tour='sidebar-opener']" },
          action: { kind: "click_target" },
          highlightOnly: true,
          bubblePlacement: "anchorBelow",
          // Sidebar opener relayouts the topbar mid-click, which makes
          // the click event's target unreliable. Permissive mode: any
          // click anywhere advances the beat. See BuddyBeat.releaseOnAnyClick.
          releaseOnAnyClick: true,
        },
      ]
    : [];

  return [
    // ── 1. Welcome — bottom-right bubble, no spotlight ───────────────────
    //
    // The viewer just landed from the replay's "View alert" link. The
    // first frame they see should be their LIVE alert page in full
    // colour, not a centered modal demanding attention. `noAnchor`
    // suppresses the entire backdrop (no ring, no dim) and
    // `bubblePlacement: "bottomRight"` pins the bubble to the lower-
    // right corner so the canvas above is fully visible.
    //
    // Voice: factual + descriptive, no salesy taglines. The previous
    // attempts ("alpha on autopilot", "watching the tape") read as
    // marketing copy. This version literally describes what the page
    // is and what the pipeline does, in trader-peer tone. Lists the
    // four things you can do here so the user has a mental map of
    // the rest of the tour before it starts.
    //
    // Placement is topCenter (not bottomRight) so the welcome reads
    // as a proper hello at the top of the screen rather than a
    // notification toast tucked away in the corner.
    {
      beatId: "alert_welcome",
      trigger: { type: "client" },
      copy: {
        title: "This is your live alert",
        body:
          "**The pipeline you just built is now running in the cloud.**\n\nEvery scheduled run will:\n\n- **Capture** the latest rendered dashboard\n- **Email** the result straight to your inbox\n- **Save** past runs so you can review them later\n\nFrom here, you can run it on demand, edit the schedule, or pause it anytime.",
        cta: "Next",
      },
      noAnchor: true,
      bubblePlacement: "topCenter",
    },

    // ── 2. Click the final dashboard node ────────────────────────────────
    //
    // We jump straight to the payoff. The trimmed alert fork is 5
    // cells now; the dashboard cell is `displayIndex={finalNodeIndex}`
    // (default 5). click_target releases the beat on native click.
    //
    // highlightOnly + click_target = no white ring, only the emerald
    // pulse + Click pill. The previous "white ring + offset + green
    // ring" stack read as three concentric boxes — fixed.
    {
      beatId: "alert_dashboard_click",
      trigger: { type: "client" },
      copy: {
        title: "The final cell",
        body:
          "Every run ends here, at the **dashboard cell**.\n\nClick to open it.",
      },
      anchor: {
        kind: "css",
        value: `[data-testid='alert-canvas-node'][data-display-index='${finalNodeIndex}']`,
      },
      action: { kind: "click_target" },
      highlightOnly: true,
      clickHintPlacement: "above",
    },

    // ── 3. Read the dashboard with scroll demo ───────────────────────────
    //
    // Modal is open. Spotlight the modal card and pair it with the
    // `modal_scroll` demo, which renders a bouncing chevron + "Scroll"
    // pill on the right edge of the spotlit area. Pure visual hint —
    // no gesture detection (the modal has its own scroll container,
    // and we don't want the tour to auto-advance when the user just
    // wants to flick through casually).
    {
      beatId: "alert_dashboard_explore",
      trigger: { type: "client" },
      copy: {
        title: "The result of your alert",
        body:
          "**The full dashboard for the latest run.** Top picks, charts, defined-risk trade tickets you can paste into a broker.\n\nScroll to see all of it.",
        cta: "Next",
      },
      anchor: { kind: "css", value: "[data-testid='alert-cell-modal']" },
      action: { kind: "continue", demo: "modal_scroll" },
      highlightOnly: true,
      bubblePlacement: "topCenter",
    },

    // ── 4. Close the dashboard ───────────────────────────────────────────
    //
    // click_target on the X button. Native click closes the modal,
    // overlay click listener releases the beat on the same event.
    // Spotlight is the modal shell so the modal stays bright during
    // the close (the small X alone wouldn't read as "close THIS").
    {
      beatId: "alert_dashboard_close",
      trigger: { type: "client" },
      copy: {
        title: "Close it",
        body: "Hit the X. There's more to see.",
      },
      anchor: { kind: "css", value: "[data-testid='alert-cell-modal-close']" },
      spotlight: { kind: "css", value: "[data-testid='alert-cell-modal']" },
      action: { kind: "click_target" },
      highlightOnly: true,
    },

    // ── 5. Click the History button ──────────────────────────────────────
    // The history icon lives in the alert top-bar's right action cluster
    // (top-right corner of the viewport). The default click_target
    // adjacency picks LEFT for top-right anchors, but on a near-top
    // rect the LEFT branch clamps the bubble against the top-left and
    // visually crowds the green ring. "anchorBelow" pins the bubble
    // directly under the icon, horizontally centered on the rect — in
    // proximity to the click target without obscuring it.
    {
      beatId: "alert_history_click",
      trigger: { type: "client" },
      copy: {
        title: "Every run is logged",
        body:
          "Every time the alert fires, Xynth saves a **full snapshot** of the dashboard.\n\nClick to open the history.",
      },
      anchor: { kind: "css", value: "[data-tour='alert-history']" },
      action: { kind: "click_target" },
      highlightOnly: true,
      bubblePlacement: "anchorBelow",
    },

    // ── 6. Read the history sidebar ──────────────────────────────────────
    //
    // AlertLogsModal is open. We anchor on the run-list SIDEBAR
    // specifically (`data-testid="alert-logs-modal-runs"`), not the
    // whole modal. Bubble pinned to top-center so it doesn't sit on
    // top of the dashboard preview in the right pane (the default
    // right-edge placement covered the rendered HTML, which the user
    // flagged as "looks horrible").
    //
    // Copy describes what's actually visible in the sidebar (time +
    // status + snapshot size per row, newest on top, click-to-load).
    // The previous copy ("scroll back through past runs") was too
    // abstract — the user can see it's a list, the value is in
    // explaining what each row IS and what clicking does.
    {
      beatId: "alert_history_explore",
      trigger: { type: "client" },
      copy: {
        title: "Every run, archived",
        body:
          "**Each row is one alert fire.**\n\nXynth saves the full dashboard from every scheduled run.\n\n- Newest run at top\n- Checkmark means clean run\n- Size shows captured dashboard HTML",
        cta: "Next",
      },
      anchor: { kind: "css", value: "[data-testid='alert-logs-modal-runs']" },
      spotlight: { kind: "css", value: "[data-testid='alert-logs-modal-runs']" },
      action: { kind: "continue" },
      highlightOnly: true,
      bubblePlacement: "outsideModalRight",
    },

    {
      beatId: "alert_history_select_previous",
      trigger: { type: "client" },
      copy: {
        title: "Open the previous run",
        body:
          "**Click the second row.**\n\nThat opens the dashboard from the run before the latest one.",
      },
      anchor: {
        kind: "css",
        value: "[data-testid='alert-logs-modal-run-row'][data-run-index='1']",
      },
      spotlight: { kind: "css", value: "[data-testid='alert-logs-modal']" },
      action: { kind: "click_target" },
      highlightOnly: true,
      bubblePlacement: "modalRight",
      clickHintPlacement: "above",
    },

    {
      beatId: "alert_history_previous_explain",
      trigger: { type: "client" },
      copy: {
        title: "A full prior dashboard",
        body:
          "**This is the previous run.**\n\nXynth preserves the rendered dashboard with its picks, charts, tickets, and summary.\n\nUse history to see what changed.",
        cta: "Next",
      },
      anchor: { kind: "css", value: "[data-testid='alert-logs-modal']" },
      spotlight: { kind: "css", value: "[data-testid='alert-logs-modal']" },
      action: { kind: "continue" },
      highlightOnly: true,
      bubblePlacement: "outsideModalRight",
    },

    // ── 7. Close the history modal ───────────────────────────────────────
    //
    // click_target on the history modal's X button. Same close pattern
    // as the dashboard close (beat #4) — modal-shell spotlight, X is
    // the click target.
    {
      beatId: "alert_history_close",
      trigger: { type: "client" },
      copy: {
        title: "Close it back up",
        body: "Hit the X to head back.",
      },
      anchor: { kind: "css", value: "[data-testid='alert-logs-modal-close']" },
      spotlight: { kind: "css", value: "[data-testid='alert-logs-modal']" },
      action: { kind: "click_target" },
      highlightOnly: true,
      bubblePlacement: "anchorLeft",
      waitUntilGone: { kind: "css", value: "[data-testid='alert-logs-modal']" },
    },

    // ── 8. Email chip ────────────────────────────────────────────────────
    //
    // Just point at it. No click_target — opening the email-edit
    // dialog mid-tour breaks the flow. The user can click the chip
    // anytime after the tour to change their email.
    {
      beatId: "alert_email_chip",
      trigger: { type: "client" },
      copy: {
        title: "Where the notifications land",
        body:
          "**Your notification email.**\n\nAfter the tour, this chip is where you change the inbox that receives each run.",
        cta: "Next",
      },
      anchor: { kind: "css", value: "[data-tour='alert-email-chip']" },
      highlightOnly: true,
      bubblePlacement: "anchor",
    },

    // ── 9. Click the Schedule chip ───────────────────────────────────────
    //
    // click_target — opens EditScheduleDialog. The dialog has both a
    // preset grid AND a plain-English natural-language input, which
    // are the next two beats.
    {
      beatId: "alert_schedule_click",
      trigger: { type: "client" },
      copy: {
        title: "How often it runs",
        body:
          "**Your schedule chip.**\n\nClick to change when the alert fires.",
      },
      anchor: { kind: "css", value: "[data-tour='alert-schedule']" },
      action: { kind: "click_target" },
      highlightOnly: true,
    },

    // ── 10. Schedule presets ─────────────────────────────────────────────
    //
    // EditScheduleDialog is open. Spotlight the preset grid first —
    // this is the "easy mode" path. 8 ready-made schedules covering
    // the common cases (every-N-min during market hours, daily,
    // weekly).
    {
      beatId: "alert_schedule_presets",
      trigger: { type: "client" },
      copy: {
        title: "Pick a preset",
        body:
          "**Eight ready-made schedules** to choose from. Pick one later when you want the fastest setup path.",
        cta: "Next",
      },
      anchor: { kind: "css", value: "[data-testid='schedule-presets']" },
      highlightOnly: true,
      bubblePlacement: "topCenter",
    },

    // ── 11. Schedule plain-English input ─────────────────────────────────
    //
    // Spotlight the natural-language section. This is the wow
    // feature — type a sentence, Gemini parses it into a real cron
    // schedule. Copy ends with "close the dialog when done" because
    // the next beat (Run Now) is in the top bar behind this dialog.
    {
      beatId: "alert_schedule_nl",
      trigger: { type: "client" },
      copy: {
        title: "Or describe it in English",
        body:
          "Type something like:\n\n`every 15 min during market hours`\n\n`daily at 3pm ET`\n\nXynth parses it into the right cron schedule.",
        cta: "Next",
      },
      anchor: { kind: "css", value: "[data-testid='schedule-nl']" },
      highlightOnly: true,
      bubblePlacement: "topCenter",
    },

    // ── 12. Close the schedule dialog ────────────────────────────────────
    {
      beatId: "alert_schedule_close",
      trigger: { type: "client" },
      copy: {
        title: "Close the schedule editor",
        body: "Hit the X to head back to the alert controls.",
      },
      anchor: { kind: "css", value: "[data-testid='schedule-dialog-close']" },
      spotlight: { kind: "css", value: "[data-testid='schedule-dialog']" },
      action: { kind: "click_target" },
      highlightOnly: true,
      bubblePlacement: "anchorBelow",
      clickHintPlacement: "above",
    },

    // ── 13. Run Now button ───────────────────────────────────────────────
    //
    // Single button, single beat. The user explicitly asked for the
    // four right-cluster controls to be explained one at a time
    // instead of crammed into one beat — this is the first.
    {
      beatId: "alert_action_run_now",
      trigger: { type: "client" },
      copy: {
        title: "Run Now",
        body:
          "Fires the alert **immediately**, on demand.\n\nGreat for testing, or for grabbing a fresh run between scheduled fires.",
        cta: "Next",
      },
      anchor: { kind: "css", value: "[data-tour='alert-action-run-now']" },
      highlightOnly: true,
      bubblePlacement: "anchor",
    },

    // ── 14. Pause button ─────────────────────────────────────────────────
    {
      beatId: "alert_action_pause",
      trigger: { type: "client" },
      copy: {
        title: "Pause",
        body:
          "**Stops the schedule** without deleting the alert.\n\nUse it later to resume right where you left off.",
        cta: "Next",
      },
      anchor: { kind: "css", value: "[data-tour='alert-action-pause']" },
      highlightOnly: true,
      bubblePlacement: "anchor",
    },

    // ── 15. Three-dot menu trigger (click) ───────────────────────────────
    //
    // click_target — opens the Radix dropdown menu. The next beat
    // anchors on the now-open menu content. Radix renders the menu
    // in a portal as soon as it opens, so by the time beat 16 fires
    // the `[data-tour='alert-action-menu-content']` selector resolves.
    //
    // When the user advances past beat 16, clicking on the bubble
    // closes the menu (Radix outside-click). That's fine — the menu
    // doesn't need to stay open beyond beat 16.
    // Bubble pinned BELOW the icon for the same reason as the history-
    // click beat: the three-dot menu lives in the top-right action
    // cluster, and the default click_target adjacency clamps the
    // bubble against the top-left, crowding the green-ringed target.
    // "anchorBelow" keeps the bubble in proximity (right under the
    // icon) without obscuring it.
    {
      beatId: "alert_action_menu_click",
      trigger: { type: "client" },
      copy: {
        title: "More options",
        body:
          "Tap the **three dots** to see everything else you can do with this alert.",
      },
      anchor: { kind: "css", value: "[data-tour='alert-action-menu']" },
      action: { kind: "click_target" },
      highlightOnly: true,
      bubblePlacement: "anchorBelow",
      // Radix DropdownMenu trigger re-targets the click event to <html>
      // mid-gesture, which made the strict click-target guard bounce
      // the user into the exit-confirm modal. Permissive mode: any
      // click anywhere advances the beat.
      releaseOnAnyClick: true,
    },

    // ── 16. Three-dot menu (open content) ────────────────────────────────
    //
    // Menu is open. We anchor on the open dropdown content so the
    // ring wraps the actual menu items. The user reads the bubble,
    // clicks Next, the menu closes via Radix outside-click — fine,
    // they've absorbed what's in there.
    {
      beatId: "alert_action_menu_open",
      trigger: { type: "client" },
      copy: {
        title: "Everything else",
        body:
          "**Share** the alert with a public link.\n\n**Rename** or change the email and schedule.\n\n**Make it public** so anyone can see the dashboard.\n\n**Delete** when you're done with it.",
        cta: "Next",
      },
      anchor: {
        kind: "css",
        value: "[data-tour='alert-action-menu-content']",
      },
      actionTarget: { kind: "css", value: "[data-tour='alert-action-menu']" },
      action: { kind: "click_on_next" },
      highlightOnly: true,
      bubblePlacement: "anchor",
      waitUntilGone: { kind: "css", value: "[data-tour='alert-action-menu-content']" },
    },

    // ── 17. Chat input ───────────────────────────────────────────────────
    //
    // The closing beat. Anchored on the floating chat pill on the
    // alert canvas (`data-tour="alert-chat-input"` lives in
    // components/artifact.tsx, rendered when the chat is minimized,
    // which is the default state on the alert page).
    //
    // After this beat the platform orientation continues in the sidebar.
    {
      beatId: "alert_chat_input",
      trigger: { type: "client" },
      copy: {
        title: "Got a question?",
        body:
          "Want to **edit your alert**, ask why a stock made the cut, change the schedule, anything?\n\n**Just type it here.** The agent knows your whole pipeline.",
        cta: "Next",
      },
      anchor: {
        kind: "css",
        value: "[data-tour='alert-chat-input'] [data-testid='multimodal-input-surface']",
      },
      highlightOnly: true,
      spotlightRadius: 22,
      bubblePlacement: "anchorTopRight",
    },
    ...sidebarOpenerBeats,
    {
      beatId: "alert_sidebar_intro",
      trigger: { type: "client" },
      copy: {
        title: "The main control panel",
        body:
          "This sidebar is the app's control panel. It is where you jump between home, new sessions, past research, live alerts, and your account.",
        cta: "Next",
      },
      anchor: { kind: "css", value: "[data-tour='sidebar-root']" },
      bubblePlacement: "anchor",
    },
    {
      beatId: "alert_sidebar_home_logo",
      trigger: { type: "client" },
      copy: {
        title: "Home",
        body: "Click the Xynth logo whenever you want to go back home.",
        cta: "Next",
      },
      anchor: { kind: "css", value: "[data-tour='sidebar-home-logo']" },
      highlightOnly: true,
      bubblePlacement: "anchor",
    },
    {
      beatId: "alert_sidebar_new_session",
      trigger: { type: "client" },
      copy: {
        title: "New Session",
        body: "This starts a fresh chat from the same home entry point.",
        cta: "Next",
      },
      anchor: { kind: "css", value: "[data-tour='new-session']" },
      highlightOnly: true,
      bubblePlacement: "anchor",
    },
    {
      beatId: "alert_sidebar_search",
      trigger: { type: "client" },
      copy: {
        title: "Search sessions",
        body: "Use this to find an old chat without scrolling through the full history.",
        cta: "Next",
      },
      anchor: { kind: "css", value: "[data-tour='sidebar-search']" },
      highlightOnly: true,
      bubblePlacement: "anchor",
    },
    {
      beatId: "alert_sidebar_research",
      trigger: { type: "client" },
      copy: {
        title: "Research",
        body: "All your chats and research sessions live here. Reopen old analyses or continue a thread.",
        cta: "Next",
      },
      anchor: { kind: "css", value: "[data-tour='sidebar-research-section']" },
      highlightOnly: true,
      bubblePlacement: "anchor",
    },
    {
      beatId: "alert_sidebar_alerts",
      trigger: { type: "client" },
      copy: {
        title: "Alerts",
        body:
          "Everything you automate lands here. These are the live pipelines that keep running after the chat is done.",
        cta: "Next",
      },
      anchor: { kind: "css", value: "[data-tour='sidebar-alerts-section']" },
      highlightOnly: true,
      bubblePlacement: "anchor",
    },
    {
      beatId: "alert_sidebar_profile",
      trigger: { type: "client" },
      copy: {
        title: "Profile",
        body: "Your account, plan, settings, and billing live down here.",
        cta: "Next",
      },
      anchor: { kind: "css", value: "[data-tour='sidebar-profile-section']" },
      highlightOnly: true,
      bubblePlacement: "anchor",
    },
    {
      beatId: "alert_sidebar_contact",
      trigger: { type: "client" },
      copy: {
        title: "Where to find us",
        body:
          "Need help or want to talk to us? The bottom links are where you can find the Xynth community and contact the team directly.",
        cta: "Next",
      },
      anchor: { kind: "css", value: "[data-tour='sidebar-contact-section']" },
      highlightOnly: true,
      bubblePlacement: "anchor",
    },
    {
      beatId: "alert_sidebar_home_handoff",
      trigger: { type: "client" },
      copy: {
        title: "Go home",
        body: "Click the Xynth logo to head back home.",
      },
      anchor: { kind: "css", value: "[data-tour='sidebar-home-logo']" },
      action: { kind: "click_target" },
      highlightOnly: true,
      bubblePlacement: "anchor",
    },
  ];
}

export function buildMobileAlertPageTourBeats(
  finalNodeIndex: number = 5,
): BuddyBeat[] {
  return [
    {
      beatId: "alert_mobile_welcome",
      trigger: { type: "client" },
      copy: {
        title: "This is your live alert",
        body:
          "**The pipeline you just built is now running in the cloud.**\n\nEvery scheduled run saves the dashboard, emails the result, and keeps a history you can review later.",
        cta: "Next",
      },
      noAnchor: true,
      bubblePlacement: "topCenter",
    },
    {
      beatId: "alert_mobile_canvas_overview",
      trigger: { type: "client" },
      copy: {
        title: "Alert canvas",
        body:
          "Each card is one step in the alert. On mobile, this page starts with the canvas so you can see the whole workflow first.",
        cta: "Next",
      },
      anchor: { kind: "css", value: "[data-testid='alert-canvas']" },
      highlightOnly: true,
      bubblePlacement: "topCenter",
    },
    {
      beatId: "alert_mobile_step_nav",
      trigger: { type: "client" },
      copy: {
        title: "Move through steps",
        body:
          "Use this control to walk through the alert one step at a time, or jump straight back to the final dashboard.",
        cta: "Next",
      },
      anchor: { kind: "css", value: "[data-tour='alert-mobile-step-nav']" },
      highlightOnly: true,
      bubblePlacement: "anchorBelow",
    },
    {
      beatId: "alert_mobile_dashboard_click",
      trigger: { type: "client" },
      copy: {
        title: "Open the dashboard",
        body:
          "Every run ends at the dashboard cell. Tap it to inspect the latest result.",
      },
      anchor: {
        kind: "css",
        value: `[data-testid='alert-canvas-node'][data-display-index='${finalNodeIndex}']`,
      },
      action: { kind: "click_target" },
      highlightOnly: true,
      clickHintPlacement: "above",
    },
    {
      beatId: "alert_mobile_dashboard_explore",
      trigger: { type: "client" },
      copy: {
        title: "Latest result",
        body:
          "This is the saved dashboard from the alert run. Scroll to review the picks, charts, trade tickets, and summary.",
        cta: "Next",
      },
      anchor: { kind: "css", value: "[data-testid='alert-cell-modal']" },
      action: { kind: "continue", demo: "modal_scroll" },
      highlightOnly: true,
      bubblePlacement: "topCenter",
    },
    {
      beatId: "alert_mobile_dashboard_close",
      trigger: { type: "client" },
      copy: {
        title: "Back to the canvas",
        body: "Close the dashboard to return to the alert.",
      },
      anchor: { kind: "css", value: "[data-testid='alert-cell-modal-close']" },
      spotlight: { kind: "css", value: "[data-testid='alert-cell-modal']" },
      action: { kind: "click_target" },
      highlightOnly: true,
    },
    {
      beatId: "alert_mobile_controls",
      trigger: { type: "client" },
      copy: {
        title: "Alert controls",
        body:
          "From here you can run the alert now, pause it, open history, or use the menu for sharing and settings.",
        cta: "Next",
      },
      anchor: { kind: "css", value: "[data-tour='alert-controls']" },
      highlightOnly: true,
      bubblePlacement: "anchorBelow",
    },
    {
      beatId: "alert_mobile_chat_input",
      trigger: { type: "client" },
      copy: {
        title: "Ask Xynth to edit it",
        body:
          "Use this input when you want to change the alert, ask what a step does, or adjust how it runs.",
        cta: "Next",
      },
      anchor: {
        kind: "css",
        value: "[data-tour='alert-chat-input'] [data-testid='multimodal-input-surface']",
      },
      highlightOnly: true,
      spotlightRadius: 22,
      bubblePlacement: "anchorAbove",
    },
  ];
}

export function buildAutomationsAlertPageTourBeats(
  options?: { includeSidebarOpener?: boolean },
): BuddyBeat[] {
  const signalDashboardNodeIndex = 6;

  return [
    {
      beatId: "alert_public_view_intro",
      trigger: { type: "client" },
      copy: {
        title: "Xynth-built signal",
        body:
          "**This is a live signal built by the Xynth team.**\n\nIt works like the alert you just created in chat — but this one is professionally built, maintained, and tracked by Xynth.",
        cta: "Next",
      },
      anchor: { kind: "css", value: "[data-testid='alert-canvas']" },
      highlightOnly: true,
      hideSpotlightRing: true,
      noScroll: true,
      bubblePlacement: "topCenter",
    },
    {
      beatId: "alert_public_signal_strategy",
      trigger: { type: "client" },
      copy: {
        title: "Insider cluster trades",
        body:
          "This signal watches for **clusters of insider buying** — multiple insiders buying around the same company.\n\nThe goal is simple: catch high-conviction insider activity before the market fully prices in the story.",
        cta: "Next",
      },
      anchor: { kind: "css", value: "[data-testid='alert-canvas']" },
      highlightOnly: true,
      hideSpotlightRing: true,
      noScroll: true,
      bubblePlacement: "topCenter",
    },
    {
      beatId: "alert_public_dashboard_click",
      trigger: { type: "client" },
      copy: {
        title: "Open the latest signal",
        body: "Click the dashboard to see the trade this signal surfaced.",
      },
      anchor: {
        kind: "css",
        value: `[data-testid='alert-canvas-node'][data-display-index='${signalDashboardNodeIndex}']`,
      },
      action: { kind: "click_target" },
      highlightOnly: true,
      clickHintPlacement: "above",
    },
    {
      beatId: "alert_public_dashboard_trade",
      trigger: { type: "client" },
      copy: {
        title: "Here’s the trade",
        body:
          "**This top section is the payoff.**\n\nIt shows the company, the insider activity, the setup, and the risk/reward context behind the signal.",
        cta: "Next",
      },
      noAnchor: true,
      action: { kind: "continue" },
      bubblePlacement: "topCenter",
      bubbleOffset: { x: 140, y: 56 },
    },
    {
      beatId: "alert_public_dashboard_close",
      trigger: { type: "client" },
      copy: {
        title: "Close it",
        body: "Hit the X to keep going.",
      },
      anchor: { kind: "css", value: "[data-testid='alert-cell-modal-close']" },
      spotlight: { kind: "css", value: "[data-testid='alert-cell-modal']" },
      action: { kind: "click_target" },
      highlightOnly: true,
    },
    {
      beatId: "alert_public_live_delayed",
      trigger: { type: "client" },
      copy: {
        title: "Live vs Delayed",
        body:
          "Subscribers get live, fresh alerts every day. Free users can view the same signals delayed by a few weeks.",
        cta: "Next",
      },
      anchor: { kind: "css", value: "[data-tour='alert-public-view-toggle']" },
      highlightOnly: true,
      bubblePlacement: "anchorBelow",
    },
    {
      beatId: "alert_public_subscribe",
      trigger: { type: "client" },
      copy: {
        title: "Get live signals",
        body:
          "Subscribe to receive fresh Xynth signals as soon as they fire, instead of waiting for the delayed public version.",
        cta: "Next",
      },
      anchor: { kind: "css", value: "[data-tour='alert-public-subscribe']" },
      highlightOnly: true,
      bubblePlacement: "anchorBelow",
    },
    ...(options?.includeSidebarOpener
      ? [
          {
            beatId: "alert_public_sidebar_open",
            trigger: { type: "client" },
            copy: {
              title: "Go back home",
              body:
                "Open the sidebar so we can head back to the Xynth home screen.",
            },
            anchor: { kind: "css", value: "[data-tour='sidebar-opener']" },
            spotlight: { kind: "css", value: "[data-tour='sidebar-opener']" },
            action: { kind: "click_target" },
            highlightOnly: true,
            bubblePlacement: "anchorBelow",
            // Sidebar opener relayouts the topbar mid-click; permissive
            // mode lets the natural click advance the beat. See
            // BuddyBeat.releaseOnAnyClick.
            releaseOnAnyClick: true,
          } satisfies BuddyBeat,
        ]
      : []),
    {
      beatId: "alert_public_home_handoff",
      trigger: { type: "client" },
      copy: {
        title: "Back home",
        body: "Click the Xynth logo to go back home and keep exploring.",
      },
      anchor: { kind: "css", value: "[data-tour='sidebar-home-logo']" },
      action: { kind: "click_target" },
      highlightOnly: true,
      bubblePlacement: "anchor",
    },
  ];
}
